// Gleaner app
import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate, useLocation } from 'react-router-dom';
import TheBox from './pages/TheBox';
import InspireMode from './pages/InspireMode';
import Archive from './pages/Archive';
import Trash from './pages/Trash';
import BulkEntry from './pages/BulkEntry';
import AddCard from './pages/AddCard';
import More from './pages/More';
import TagManager from './pages/TagManager';
import FindDuplicates from './pages/FindDuplicates';
import Import from './pages/Import';
import Export from './pages/Export';
import Login from './pages/Login';
import './App.css';

function MoreDropdown({ onLogout }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <div className="more-dropdown-wrap" ref={wrapRef}>
      <button
        className={`more-dropdown-btn${open ? ' open' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        More ▾
      </button>
      {open && (
        <div className="more-dropdown-menu">
          <button className="more-dropdown-item" onClick={() => go('/import')}>Import</button>
          <button className="more-dropdown-item" onClick={() => go('/export')}>Export</button>
          <button className="more-dropdown-item" onClick={() => go('/tags')}>Manage Tags</button>
          <button className="more-dropdown-item" onClick={() => go('/duplicates')}>Find Duplicates</button>
          <div className="more-dropdown-divider" />
          <button className="more-dropdown-item" onClick={() => go('/archive')}>Archive</button>
          <button className="more-dropdown-item" onClick={() => go('/trash')}>Trash</button>
          <div className="more-dropdown-divider" />
          <button className="more-dropdown-item danger" onClick={() => { setOpen(false); onLogout(); }}>Log Out</button>
        </div>
      )}
    </div>
  );
}

function Layout({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();

  const isMoreSection = ['/archive', '/trash', '/bulk', '/more', '/tags', '/duplicates', '/import', '/export'].includes(location.pathname);

  return (
    <div className="app">
      {/* DESKTOP HEADER */}
      <header className="header">
        <div className="header-inner">
          <span className="logo" onClick={() => navigate('/')}>Gleaner</span>
          <nav className="nav desktop-nav">
            <NavLink to="/add">Add Card</NavLink>
            <NavLink to="/" end>All Cards</NavLink>
            <NavLink to="/inspire">Glean</NavLink>
            <MoreDropdown onLogout={onLogout} />
          </nav>
        </div>
      </header>

      <main className="main">
        <Routes>
          <Route path="/" element={<TheBox />} />
          <Route path="/inspire" element={<InspireMode />} />
          <Route path="/add" element={<AddCard />} />
          <Route path="/bulk" element={<BulkEntry />} />
          <Route path="/archive" element={<Archive />} />
          <Route path="/trash" element={<Trash />} />
          <Route path="/more" element={<More onLogout={onLogout} />} />
          <Route path="/tags" element={<TagManager />} />
          <Route path="/duplicates" element={<FindDuplicates />} />
          <Route path="/import" element={<Import />} />
          <Route path="/export" element={<Export />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>

      {/* MOBILE BOTTOM TAB BAR */}
      <nav className="mobile-tab-bar">
        <NavLink to="/add" onClick={e => { if (window.__gleanNavGuard) { e.preventDefault(); window.__gleanNavGuard('/add'); } }} className={({ isActive }) => `tab-item tab-item-add ${isActive ? 'active' : ''}`}>
          <span className="tab-icon tab-add-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
          </span>
          <span className="tab-label">Add</span>
        </NavLink>

        <NavLink to="/" end onClick={e => { if (window.__gleanNavGuard) { e.preventDefault(); window.__gleanNavGuard('/'); } }} className={({ isActive }) => `tab-item ${isActive ? 'active' : ''}`}>
          <span className="tab-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/>
              <rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
          </span>
          <span className="tab-label">All Cards</span>
        </NavLink>

        <NavLink to="/inspire" className={({ isActive }) => `tab-item ${isActive ? 'active' : ''}`}>
          <span className="tab-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
          </span>
          <span className="tab-label">Glean</span>
        </NavLink>

        <NavLink to="/more" onClick={e => { if (window.__gleanNavGuard) { e.preventDefault(); window.__gleanNavGuard('/more'); } }} className={({ isActive }) => `tab-item ${isActive || isMoreSection ? 'active' : ''}`}>
          <span className="tab-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>
            </svg>
          </span>
          <span className="tab-label">More</span>
        </NavLink>
      </nav>
    </div>
  );
}

export default function App() {
  const [authenticated, setAuthenticated] = useState(null);

  useEffect(() => {
    fetch('/api/auth/check', { credentials: 'include' })
      .then(r => r.json())
      .then(d => setAuthenticated(d.authenticated))
      .catch(() => setAuthenticated(false));
  }, []);

  const handleLogin = () => setAuthenticated(true);
  const handleLogout = () => {
    fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
      .then(() => setAuthenticated(false));
  };

  if (authenticated === null) return <div className="loading">Loading...</div>;
  if (!authenticated) return <Login onLogin={handleLogin} />;

  return (
    <BrowserRouter>
      <Layout onLogout={handleLogout} />
    </BrowserRouter>
  );
}
